/* eslint-disable indent */
import React, { useState } from "react";
import PropTypes from "prop-types";
import { TiPencil } from "react-icons/ti";
import { useDispatch, useSelector } from "react-redux";
import t from "../../../Common/utils/translation/translator";
import EditAxisPopup from "./EditAxisPopup";
import {
  selectItemToHighlight,
  selectRetrofitConfiguration,
  setItemToHighlight
} from "../../../Retrofit/store/slices/retrofit";
import AxisFiltersRecapLine from "./AxisFiltersRecapLine";
import useCheckMobileScreen from "../../../Common/utils/useCheckMobileScreen";
import { logEvent } from "../../../Common/utils/api/api";
import { selectEventsData } from "../../../Common/store/slices/common";

const AxisFiltersRecap = ({ axis, print }) => {
  const [editAxisPopupOpened, setEditAxisPopupOpened] = useState(false);
  const itemToHighlight = useSelector(selectItemToHighlight);

  const toNumber = (string) => {
    if (typeof string === "number") {
      return string;
    }
    const number = string?.replace(",", ".");
    // eslint-disable-next-line no-restricted-globals
    if (isNaN(number) && number !== "") {
      return NaN;
    }
    return parseFloat(number);
  };

  const eventsData = useSelector(selectEventsData);
  const configuration = useSelector(selectRetrofitConfiguration);

  const handleOpenEditAxisPopup = () => {
    setEditAxisPopupOpened(true);
    logEvent(eventsData.locale, eventsData.types.editInput, eventsData.tags.none, configuration?.configId);
  };

  const dispatch = useDispatch();

  const isMobile = useCheckMobileScreen();

  if (isMobile && !print) {
    return (
      <>
        <div className="grid grid-cols-[2fr_1fr_1fr] gap-y-2 ">
          <span>{t("msizer.ui.select.application_data")}</span>
          <div className="flex items-center gap-1 justify-center">
            <span>{t("msizer.ui.select.input")}</span>
            <TiPencil
              onClick={(e) => {
                e.stopPropagation();
                setEditAxisPopupOpened(true);
              }}
              onMouseEnter={() => {
                dispatch(setItemToHighlight(`all-${axis.id}`));
              }}
              onMouseLeave={() => {
                dispatch(setItemToHighlight(null));
              }}
              className="text-secondary
 cursor-pointer"
            />
          </div>
          <span className="text-center font-medium">{t("msizer.ui.select.output")}</span>

          <div className="flex gap-2">
            <span className="text-gray-500 whitespace-nowrap">{t("msizer.ui.nominal_torque")}</span>
            <span className="text-gray-400">{`(${t("msizer.ui.unit.nm")})`}</span>
          </div>
          <span className="flex gap-1 justify-center">
            {axis.configFilters?.applicationDataFilter?.nominalTorque
              ? axis.configFilters?.applicationDataFilter?.nominalTorque?.toString()
              : "--"}
          </span>
          <span className="justify-self-center font-medium">
            {axis.bundle?.bundleData?.nominalTorque
              ? Number(parseFloat(axis.bundle?.bundleData?.nominalTorque).toFixed(2))
              : "--"}
          </span>

          <div className="flex gap-2">
            <span className="text-gray-500 whitespace-nowrap">{t("msizer.ui.nominal_speed")}</span>
            <span className="text-gray-400">{`(${t("msizer.ui.unit.rpm")})`}</span>
          </div>
          <span className="flex gap-1 justify-center">
            {axis.configFilters?.applicationDataFilter?.nominalSpeed
              ? axis.configFilters?.applicationDataFilter?.nominalSpeed?.toString()
              : "--"}
          </span>
          <span className="justify-self-center font-medium">
            {axis.bundle?.bundleData?.nominalSpeed
              ? Number(parseFloat(axis.bundle?.bundleData?.nominalSpeed).toFixed(2))
              : "--"}
          </span>

          <div className="flex gap-2">
            <span className="text-gray-500 whitespace-nowrap">{t("msizer.ui.load_inertia")}</span>
            <span className="text-gray-400">{`(${t("msizer.ui.unit.kg_cm_squared")})`}</span>
          </div>
          <span className="flex gap-1 justify-center">
            {axis.configFilters?.applicationDataFilter?.loadInertia
              ? axis.configFilters?.applicationDataFilter?.loadInertia?.toString()
              : "--"}
          </span>
          <span className="justify-self-center font-medium">
            {axis.bundle?.bundleData?.inertia ? Number(parseFloat(axis.bundle?.bundleData?.inertia).toFixed(2)) : "--"}
          </span>

          <div className="flex gap-2">
            <span className="text-gray-500 whitespace-nowrap">{t("msizer.ui.max_length")}</span>
            <span className="text-gray-400">{`(${t("msizer.ui.unit.mm")})`}</span>
          </div>
          <span className="flex gap-1 justify-center">
            {axis.configFilters?.applicationDataFilter?.maxLength
              ? axis.configFilters?.applicationDataFilter?.maxLength?.toString()
              : "--"}
          </span>
          <span className="justify-self-center font-medium">
            {axis.bundle?.bundleData?.length ? Number(parseFloat(axis.bundle?.bundleData?.length).toFixed(2)) : "--"}
          </span>
        </div>

        <EditAxisPopup
          opened={editAxisPopupOpened}
          axisId={axis.id}
          axis={axis}
          setEditAxisPopupOpened={setEditAxisPopupOpened}
        />
      </>
    );
  }

  return (
    <>
      <div className="col-span-5 lg:col-span-4 grid grid-cols-5 items-center py-2 justify-between">
        <div className="flex flex-1 flex-col items-end text-xs print:text-xxs text-gray-600">
          <div className="flex items-center space-x-1">
            <span className="print:whitespace-nowrap hidden xl:block xl:visible">
              {t("msizer.ui.select.application_input")}
            </span>
            <span className="xl:hidden">{t("msizer.ui.select.input")}</span>
            <div className="hover:underline">
              <TiPencil
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenEditAxisPopup();
                }}
                onMouseEnter={() => {
                  dispatch(setItemToHighlight(`all-${axis.id}`));
                }}
                onMouseLeave={() => {
                  dispatch(setItemToHighlight(null));
                }}
                className="text-secondary cursor-pointer print:hidden"
              />
            </div>
          </div>
          <span className="font-bold mr-4">{t("msizer.ui.select.output")}</span>
        </div>

        <div className="col-span-4 flex flex-col">
          {/* input values */}
          <AxisFiltersRecapLine
            axisId={axis.id}
            type="input"
            setEditAxisPopupOpened={setEditAxisPopupOpened}
            data={[
              {
                filterName: t("msizer.ui.nominal_torque"),
                highlight: itemToHighlight === "nominal_torque",
                value: toNumber(axis.configFilters?.applicationDataFilter?.nominalTorque),
                unit: t("msizer.ui.unit.nm")
              },
              {
                filterName: t("msizer.ui.nominal_speed"),
                highlight: itemToHighlight === "nominal_speed",
                value: toNumber(axis.configFilters?.applicationDataFilter?.nominalSpeed),
                unit: t("msizer.ui.unit.rpm")
              },
              {
                filterName: t("msizer.ui.load_inertia"),
                highlight: itemToHighlight === "load_inertia",
                value: toNumber(axis.configFilters?.applicationDataFilter?.loadInertia),
                unit: t("msizer.ui.unit.kg_cm_squared")
              },
              {
                filterName: t("msizer.ui.max_length"),
                highlight: itemToHighlight === "max_length",
                value: toNumber(axis.configFilters?.applicationDataFilter?.maxLength),
                unit: t("msizer.ui.unit.mm")
              }
            ]}
          />

          {/* output values */}
          <AxisFiltersRecapLine
            axisId={axis.id}
            type="result"
            data={[
              {
                filterName: t("msizer.ui.nominal_torque"),
                highlight: itemToHighlight === "nominal_torque",
                value: axis.bundle?.bundleData?.nominalTorque?.toString(),
                unit: t("msizer.ui.unit.nm")
              },
              {
                filterName: t("msizer.ui.nominal_speed"),
                highlight: itemToHighlight === "nominal_speed",
                value: axis.bundle?.bundleData?.nominalSpeed?.toString(),
                unit: t("msizer.ui.unit.rpm")
              },
              {
                filterName: t("msizer.ui.load_inertia"),
                highlight: itemToHighlight === "load_inertia",
                value: axis.bundle?.bundleData?.inertia?.toString(),
                unit: t("msizer.ui.unit.kg_cm_squared")
              },
              {
                filterName: t("msizer.ui.max_length"),
                highlight: itemToHighlight === "max_length",
                value: axis.bundle?.bundleData?.length?.toString(),
                unit: t("msizer.ui.unit.mm")
              }
            ]}
          />
        </div>

        {/* <AxisFiltersRecapItem
          axisId={axis.id}
          filterName={t("msizer.ui.nominal_torque")}
          highlight={itemToHighlight === "nominal_torque"}
          filterValue={axis.configFilters?.applicationDataFilter?.nominalTorque?.toString()}
          resultValue={axis.bundle?.bundleData?.nominalTorque?.toString()}
          unit={t("msizer.ui.unit.nm")}
        />

        <AxisFiltersRecapItem
          axisId={axis.id}
          filterName={t("msizer.ui.nominal_speed")}
          highlight={itemToHighlight === "nominal_speed"}
          filterValue={axis.configFilters?.applicationDataFilter?.nominalSpeed?.toString()}
          resultValue={axis.bundle?.bundleData?.nominalSpeed?.toString()}
          unit={t("msizer.ui.unit.rpm")}
        />
        <AxisFiltersRecapItem
          axisId={axis.id}
          filterName={t("msizer.ui.load_inertia")}
          highlight={itemToHighlight === "load_inertia"}
          filterValue={axis.configFilters?.applicationDataFilter?.loadInertia?.toString()}
          resultValue={axis.bundle?.bundleData?.inertia?.toString()}
          unit={t("msizer.ui.unit.kg_cm_squared")}
        />

        <AxisFiltersRecapItem
          axisId={axis.id}
          filterName={t("msizer.ui.max_length")}
          highlight={filterRecapToHighlight === "max_length"}
          filterValue={axis.configFilters?.applicationDataFilter?.maxLength?.toString()}
          resultValue={axis.bundle?.bundleData?.length?.toString()}
          unit={t("msizer.ui.unit.mm")}
        /> */}
      </div>
      <EditAxisPopup
        opened={editAxisPopupOpened}
        axisId={axis.id}
        axis={axis}
        setEditAxisPopupOpened={setEditAxisPopupOpened}
      />
    </>
  );
};

AxisFiltersRecap.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  axis: PropTypes.any.isRequired,
  print: PropTypes.bool
};

AxisFiltersRecap.defaultProps = {
  print: undefined
};

export default AxisFiltersRecap;
